import dayjs from "dayjs";
import { IPatientRecord } from "@base/modules/user/profile/profile.interface";
import { IAppointment } from "./appointment.interface";

/**
 * Display rules of the appointment detail screen, as the apps apply them. The backend sends half of
 * these fields as JSON strings and the other half as objects, so every read here is defensive.
 */

type TObject = Record<string, unknown>;

const trim = (value?: unknown) => (typeof value === "string" ? value.trim() : "");

export const parseObject = (value: unknown): TObject => {
  if (!value) {
    return {};
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    return value as TObject;
  }

  if (typeof value !== "string") {
    return {};
  }

  try {
    const parsed = JSON.parse(value);

    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

export const parseArray = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== "string" || !value.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const usedServices = (appointment: IAppointment) => appointment.appointment_used_services ?? [];

export const isCancelledAppointment = (appointment: IAppointment) => trim(appointment.status).toUpperCase() === "CANCELLED";

/** `payment_status: null` counts as paid, as on the apps. */
export const isUnpaidAppointment = (appointment: IAppointment) => appointment.payment_status === false && !isCancelledAppointment(appointment);

export const isPackageAppointment = (appointment: IAppointment & { package_type?: string }) =>
  trim(appointment.package_type).toUpperCase() === "PACKAGE" ||
  usedServices(appointment).some((service) => trim(service.package_type).toUpperCase() === "PACKAGE" || !!service.patient_special_package_detail_implement_id);

/**
 * Only a package booking shows a phone, and only the contact one - never the patient's own number.
 * The appointment's short record may lack it, so the account's full records are the last resort.
 */
export const getContactPhone = (appointment: IAppointment, fullRecords: IPatientRecord[] = []) => {
  if (!isPackageAppointment(appointment)) {
    return "";
  }

  const record = appointment.patient_record;
  const full = fullRecords.find(
    (item) => (!!trim(record?.code) && item.code === record?.code) || (record?.id !== undefined && record?.id !== null && item.id === record.id),
  );

  return trim(appointment.contact_phone) || trim(record?.contact_phone) || trim(full?.contact_phone);
};

/* ------------------------------------------------------------------ status, kind and time */

export interface IStatusStyle {
  /** Translation key of the label. */
  labelKey: string;
  text: string;
  background: string;
}

/** [text, background] - sampled from the current mobile app's status pills. */
const STATUS_PALETTE: Record<string, [string, string]> = {
  PENDING: ["#4C7FFF", "#ECF3FF"],
  IN_PROGRESS: ["#42AE5D", "#EBF7F0"],
  COMPLETED: ["#5C6B82", "#ECEFF4"],
  CANCELLED: ["#E25554", "#FCEFEF"],
};

/** The status pill, as on the apps. An unknown status takes the completed tone. */
export const getStatusStyle = (status?: string): IStatusStyle => {
  const key = trim(status).toUpperCase();
  const [text, background] = STATUS_PALETTE[key] ?? STATUS_PALETTE.COMPLETED;

  return { labelKey: STATUS_PALETTE[key] ? `appointmentDetail.status${key}` : "", text, background };
};

export type TAppointmentKind = "HOME_OR_FACILITY" | "HOME" | "ONLINE" | "FACILITY";

/** Read off the first service only. HOMECARE_OR_OFFLINE_SERVICE also contains "HOMECARE", so it is checked first. */
export const getAppointmentKind = (appointment: IAppointment): TAppointmentKind => {
  const serviceType = trim(usedServices(appointment)[0]?.service_type).toUpperCase();

  if (serviceType === "HOMECARE_OR_OFFLINE_SERVICE") {
    return "HOME_OR_FACILITY";
  }

  if (serviceType.includes("HOMECARE")) {
    return "HOME";
  }

  return appointment.appointment_type === "ONLINE" ? "ONLINE" : "FACILITY";
};

const TIME_ZONE = "Asia/Ho_Chi_Minh";
const VI_WEEKDAYS: Record<string, string> = { Sun: "CN", Mon: "Thứ 2", Tue: "Thứ 3", Wed: "Thứ 4", Thu: "Thứ 5", Fri: "Thứ 6", Sat: "Thứ 7" };

/**
 * "08:30 Thứ 2, 5/10/2026" - day and month without leading zeros; English names the weekday in
 * full. Always Vietnam time, whatever the browser's zone.
 */
export const formatSlot = (startTime: unknown, lang?: string) => {
  const ms = typeof startTime === "number" ? startTime : Number(startTime);

  if (startTime === null || startTime === undefined || startTime === "" || !Number.isFinite(ms)) {
    return "";
  }

  const date = new Date(ms);
  const part = (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat("en-GB", { timeZone: TIME_ZONE, ...options }).format(date);
  const time = part({ hour: "2-digit", minute: "2-digit", hour12: false });
  const day = Number(part({ day: "numeric" }));
  const month = Number(part({ month: "numeric" }));
  const year = part({ year: "numeric" });
  const weekday =
    lang === "en"
      ? new Intl.DateTimeFormat("en-US", { timeZone: TIME_ZONE, weekday: "long" }).format(date)
      : VI_WEEKDAYS[new Intl.DateTimeFormat("en-US", { timeZone: TIME_ZONE, weekday: "short" }).format(date)];

  return `${time} ${weekday}, ${day}/${month}/${year}`;
};

/** A paid appointment that was not free has to be cancelled through the call centre. */
export const isCancelBlockedByPayment = (appointment: IAppointment) => appointment.payment_status === true && appointment.is_free !== true;

/* ------------------------------------------------------------------ service address card */

export interface IServiceUsageCard {
  name: string;
  address: string;
  /** A clinic (true) or the patient's home (false). */
  isFacility: boolean;
}

/** A home address from the address book: its `full` text, else street, ward and province. Plain text is shown as is. */
export const homeAddressText = (raw?: string) => {
  if (!trim(raw)) {
    return "";
  }

  const parsed = parseObject(raw);

  if (!Object.keys(parsed).length) {
    return trim(raw);
  }

  const pick = (...keys: string[]) => keys.map((key) => trim(parsed[key])).find(Boolean) ?? "";

  return (
    pick("full") ||
    [
      pick("customer_delivery_address", "address"),
      pick("customer_delivery_ward_name", "ward_name"),
      pick("customer_delivery_province_name", "province_name"),
    ]
      .filter(Boolean)
      .join(", ")
  );
};

const rawFacilityAddress = (appointment: IAppointment & { facility_address?: string; site_facility_address?: string }) =>
  usedServices(appointment)
    .map((service) => trim(service.facility_address))
    .find(Boolean) ||
  trim(appointment.facility_address) ||
  trim(appointment.site_facility_address);

const rawCustomerAddress = (appointment: IAppointment) => {
  for (const service of usedServices(appointment)) {
    if (trim(service.customer_address)) {
      return trim(service.customer_address);
    }

    // Bookings made before 04/09/2026 kept it inside extra_info.
    const legacy = trim(parseObject(service.extra_info).customer_address);

    if (legacy) {
      return legacy;
    }
  }

  return "";
};

/** The doctor's partner clinics, used to put a name on a plain facility address. */
const doctorFacilities = (appointment: IAppointment) => {
  const value = parseObject(appointment.doctor?.extra_info).facilities;
  const list = typeof value === "string" ? parseArray(value) : Array.isArray(value) ? value : value && typeof value === "object" ? [value] : [];

  return (list as TObject[]).map((item) => ({ name: trim(item?.name), address: trim(item?.address) })).filter((item) => item.address);
};

const isHomecare = (appointment: IAppointment) =>
  usedServices(appointment).some((service) => ["HOMECARE_SERVICE", "HOMECARE"].includes(trim(service.service_type).toUpperCase()));

/** The appointment does not say which customer group it belongs to, so ABIC is recognised by its site. */
const isAbicHomecare = (appointment: IAppointment) => isHomecare(appointment) && trim(appointment.site_code).toUpperCase() === "INSMART";

const profileAddressText = (appointment: IAppointment) => {
  const record = appointment.patient_record as (IAppointment["patient_record"] & TObject) | undefined;

  return [record?.patient_address, record?.administrative_ward_name, record?.administrative_province_name].map(trim).filter(Boolean).join(", ");
};

/**
 * Where the service actually happens. A facility address always wins (the patient goes there);
 * otherwise only an ABIC home visit has a card, falling back to the address on the record.
 */
export const getServiceUsageCard = (appointment: IAppointment): IServiceUsageCard | null => {
  const facility = rawFacilityAddress(appointment);

  if (facility) {
    if (facility.startsWith("{")) {
      const parsed = parseObject(facility);
      const name = trim(parsed.name);
      const address = trim(parsed.address) || trim(parsed.full);

      if (name || address) {
        return { name, address: address || name, isFacility: true };
      }
    }

    const match = doctorFacilities(appointment).find((item) => item.address.toLowerCase() === facility.toLowerCase());

    return { name: match?.name ?? "", address: facility, isFacility: true };
  }

  if (!isAbicHomecare(appointment)) {
    return null;
  }

  const address = homeAddressText(rawCustomerAddress(appointment)) || profileAddressText(appointment);

  return address ? { name: "", address, isFacility: false } : null;
};

/* ------------------------------------------------------------------ extra fields */

/** A stored value: plain text, an object, or a JSON object in a string (an address, a site, a specialty). */
export const displayExtraValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }

  let object: unknown = value;

  if (typeof value === "string") {
    const text = value.trim();

    if (!text.startsWith("{")) {
      return text;
    }

    const parsed = parseObject(text);

    if (!Object.keys(parsed).length) {
      return text;
    }

    object = parsed;
  }

  if (typeof object !== "object") {
    return String(object);
  }

  const field = (key: string) => trim((object as TObject)[key]);

  return (
    field("site_name") ||
    field("description") ||
    field("specialization_description") ||
    field("specialization_name") ||
    // An address reads province first, then ward - the reverse of a record's address.
    [field("administrative_province_name"), field("administrative_ward_name")].filter(Boolean).join(", ")
  );
};

/** What the package asked for at booking time, labelled from the saved data, in the order it was saved. */
export const getExtraFieldItems = (appointment: IAppointment, lang?: string) => {
  const raw = usedServices(appointment)
    .map((service) => service.extra_fields)
    .find((item) => !!trim(item));

  return (parseArray(raw) as TObject[]).flatMap((item) => {
    const value = displayExtraValue(item?.value);

    if (!value) {
      return [];
    }

    const label = item.label;
    const labels: Record<string, string> =
      typeof label === "string"
        ? { vi: label, en: label }
        : Object.fromEntries(Object.entries((label ?? {}) as TObject).map(([key, text]) => [key.toLowerCase(), String(text ?? "")]));

    return [{ label: [lang, "vi", "en"].map((key) => trim(labels[key ?? ""])).find(Boolean) ?? trim(item.field_name), value }];
  });
};

/* ------------------------------------------------------------------ group exam items */

export interface IGroupExamItem {
  id?: number | string;
  code?: string;
  item_code?: string;
  name?: string;
  description?: string;
  amount?: number;
  sequence?: number;
  mandatory?: boolean;
  selected?: boolean;
}

const GROUP_EXAM_KEYS = ["special_package_items", "patient_special_package_items", "appointment_special_package_items", "selected_service_items"];

/** The group-exam services picked at booking: sent with the booking first, else the arrays the backend may add. */
export const getGroupExamItems = (appointment: IAppointment): IGroupExamItem[] => {
  for (const service of usedServices(appointment)) {
    const items = parseArray(service.service_items) as IGroupExamItem[];

    if (items.length) {
      return items;
    }
  }

  for (const key of GROUP_EXAM_KEYS) {
    const items = (appointment as TObject)[key] as IGroupExamItem[] | undefined;

    if (Array.isArray(items) && items.length) {
      return [...items].sort((a, b) => (a.sequence ?? Infinity) - (b.sequence ?? Infinity));
    }
  }

  return [];
};

/* ------------------------------------------------------------------ transfer, cancel errors, hotline */

export const DEFAULT_HOTLINE = "19009204";

/** "Thanh toan lich hen - 05/10/2026 - Nguyen Van A - 0912345678" - what the patient writes on the transfer; the contact number comes first. */
export const getTransferContent = (appointment: IAppointment) =>
  [
    "Thanh toan lich hen",
    appointment.appointment_date ? dayjs(appointment.appointment_date, "YYYY-MM-DD").format("DD/MM/YYYY") : "",
    trim(appointment.patient_record?.patient_name),
    trim(appointment.patient_record?.contact_phone) || trim(appointment.patient_record?.patient_phone_number),
  ]
    .filter(Boolean)
    .join(" - ");

/** The cancel cap of a group-exam package comes back as a business error in any of these fields. */
export const isCancelLimitReached = (body: unknown) => {
  const object = parseObject(body);

  return ["message", "error_code", "code", "error"].some((key) => String(object[key] ?? "").toUpperCase().includes("CANCEL_LIMIT_REACHED"));
};

/** `tel:` target: 1900/1800 numbers are dialled as they are, a leading 84 gets its plus. */
export const hotlineHref = (number: string) => {
  const digits = number.replace(/[^\d+]/g, "");

  if (/^(1900|1800)/.test(digits)) {
    return `tel:${digits}`;
  }

  return `tel:${digits.startsWith("84") ? `+${digits}` : digits}`;
};
